import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import ContextMenu from './ContextMenu'
import { useContextMenu } from '../hooks/useContextMenu'
import AudioTrimDialog from './AudioTrimDialog'
import { audioAPI } from '../../services/api'

// Interactive timeline with simple playhead, drag and trim for clips
export default function Timeline({
  template_content,
  currentPageIndex,
  settemplate_content,
  zoom = 1,
  onPlayheadChange,
  onPlayingChange,
  onExpandedChange,
  selectedElement,
  setSelectedElement,
  selectedAudioTrack,
  setSelectedAudioTrack,
  height = 280,
  onHeightChange,
}) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [zoomSeconds, setZoomSeconds] = useState(1) // Zoom in seconds (default 1s)
  const [timelineStart, setTimelineStart] = useState(0) // Timeline start offset in seconds (default 0s)
  const [horizontalScroll, setHorizontalScroll] = useState(0) // Horizontal scroll position
  const page = template_content?.pages?.[currentPageIndex]
  const items = page?.items || []

  // Separate visual and non-visual items
  const visualItems = items.filter(item => item.isVisual !== false) || []
  const nonVisualItems = items.filter(item => item.isVisual === false) || []

  // Group visual items by layer
  const visualTracks = useMemo(() => {
    const tracks = new Map()
    visualItems.forEach((item, idx) => {
      const layer = item.layer || 0
      if (!tracks.has(layer)) {
        tracks.set(layer, [])
      }
      const normalizedItem = {
        ...item,
        start: item.time?.start || 0,
        duration: item.time?.duration || 2,
        originalIndex: idx
      }
      tracks.get(layer).push(normalizedItem)
    })

    // Convert to sorted array
    return Array.from(tracks.entries())
      .sort(([a], [b]) => a - b)
      .map(([layer, items]) => ({ layer, items, type: 'visual' }))
  }, [visualItems])

  // Group non-visual items by track
  const nonVisualTracks = useMemo(() => {
    const tracks = new Map()
    nonVisualItems.forEach((item, idx) => {
      const trackId = item.trackId || 0
      if (!tracks.has(trackId)) {
        tracks.set(trackId, [])
      }
      const normalizedItem = {
        ...item,
        start: item.time?.start || item.start || 0,
        duration: item.time?.duration || item.duration || 5,
        originalIndex: idx
      }
      tracks.get(trackId).push(normalizedItem)
    })

    // Convert to sorted array
    return Array.from(tracks.entries())
      .sort(([a], [b]) => a - b)
      .map(([trackId, items]) => ({ trackId, items, type: 'non-visual' }))
  }, [nonVisualItems])

  // Combine all tracks: visual first, then non-visual
  const allTracks = [...visualTracks, ...nonVisualTracks]

  // Calculate pixels per second based on zoomSeconds
  // If zoomSeconds is 1, pxPerSec = 50 (50px per second)
  // If zoomSeconds is 2, pxPerSec = 25 (items appear half width, same pixel distance = 2 seconds)
  const pxPerSec = 50 / zoomSeconds

  // Calculate actual max end time from items
  const actualMaxEndTime = useMemo(() => {
    if (items.length === 0) return 5 // Default minimum
    return Math.max(
      5,
      ...items.map(it => (it.time?.start || 0) + (it.time?.duration || 2))
    )
  }, [items])

  // Timeline duration - show at least 120 seconds for editing, but display actual max end time
  const duration = useMemo(() => {
    return Math.max(120, actualMaxEndTime)
  }, [actualMaxEndTime])

  const seconds = Math.ceil(duration)

  const [playhead, setPlayhead] = useState(0)

  // Audio trim dialog state
  const [trimDialogOpen, setTrimDialogOpen] = useState(false)
  const [trimAudioItem, setTrimAudioItem] = useState(null)

  // Removed drag and drop state - no longer using up-down drag for layer changes

  // Context menu hook
  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu()

  // Notify parent when expanded state changes
  useEffect(() => {
    if (onExpandedChange) {
      onExpandedChange(isExpanded);
    }
  }, [isExpanded, onExpandedChange]);

  // Keyboard shortcuts for timeline zoom (disabled - using seconds-based zoom now)
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if (e.ctrlKey || e.metaKey) {
  //       if (e.key === '=' || e.key === '+') {
  //         e.preventDefault()
  //         // Zoom in: decrease seconds (e.g., 1s -> 0.5s)
  //       } else if (e.key === '-') {
  //         e.preventDefault()
  //         // Zoom out: increase seconds (e.g., 1s -> 2s)
  //       } else if (e.key === '0') {
  //         e.preventDefault()
  //         setZoomSeconds(1)
  //       }
  //     }
  //   }
  //   if (isExpanded) {
  //     window.addEventListener('keydown', handleKeyDown)
  //     return () => window.removeEventListener('keydown', handleKeyDown)
  //   }
  // }, [isExpanded, zoomSeconds]);
  const [playing, setPlaying] = useState(false)
  const [playingWithAudio, setPlayingWithAudio] = useState(false)
  const [audioLoading, setAudioLoading] = useState(false)
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null)
  const previewAudioRef = useRef(null)
  const [loop, setLoop] = useState(true)
  const [snap, setSnap] = useState(true)
  const [gridSize, setGridSize] = useState(0.1)
  const [snapToClips, setSnapToClips] = useState(true)
  const [snapToPlayhead, setSnapToPlayhead] = useState(true)
  const [showGrid, setShowGrid] = useState(false)
  const rafRef = useRef(null)
  // Store layer states in page config instead of timeline
  const layerStates = template_content?.pages?.[currentPageIndex]?.layerStates || {}
  const getLayerState = (layer) => layerStates[layer] || { muted: false, locked: false }
  const setLayerState = (layer, patch) => {
    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, idx) =>
        idx === currentPageIndex
          ? {
              ...page,
              layerStates: {
                ...(page.layerStates || {}),
                [layer]: { ...(layerStates[layer] || { muted: false, locked: false }), ...patch }
              }
            }
          : page
      )
    }
    settemplate_content(updatedTemplate)
  }

  const lastTsRef = useRef(0)
  const rootRef = useRef(null)
  const tracksRef = useRef(null)

  useEffect(() => {
    if (!playing) return
    lastTsRef.current = performance.now()
    const tick = (ts) => {
      const dt = (ts - lastTsRef.current) / 1000
      lastTsRef.current = ts
      setPlayhead(prev => {
        let next = prev + dt
        if (next >= duration) next = loop ? 0 : duration
        onPlayheadChange?.(next)
        return next
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, duration, loop, onPlayheadChange])

  const setItemTime = (idx, patch) => {
    // Find the item by index in visualItems array
    const visualItem = visualItems[idx]
    if (!visualItem) return
    
    // Find the item in the full items array by ID
    const next = { ...template_content, pages: [...(template_content.pages||[])] }
    const p = { ...(next.pages[currentPageIndex] || {}), items: [...(page?.items||[])] }
    next.pages[currentPageIndex] = p
    
    // Update the item by finding it in the full items array
    const itemIndex = p.items.findIndex(item => item.id === visualItem.id)
    if (itemIndex >= 0) {
      const it = { ...p.items[itemIndex], time: { ...(p.items[itemIndex].time||{}), ...patch } }
      p.items[itemIndex] = it
      settemplate_content(next)
    }
  }

  const collectSnapPoints = () => {
    // gather all clip edges in seconds
    const points = new Set([0, duration])
    items.forEach((it, idx) => {
      const s = (it.time?.start || idx*0.1)
      const d = (it.time?.duration || 2)
      points.add(s)
      points.add(s + d)
    })
    return Array.from(points).sort((a,b)=>a-b)
  }

  const setAudioClip = (idx, patch) => {
    // Find the audio item in page items by index
    const audioItem = nonVisualItems[idx]
    if (!audioItem) return

    // Update the audio item in page items
    // IMPORTANT: When resizing on timeline, ONLY update time.start and time.duration
    // NEVER modify trim.start or trim.end - those define the source audio segment
    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, pageIdx) =>
        pageIdx === currentPageIndex
          ? {
              ...page,
              items: page.items.map(item => {
                if (item.id === audioItem.id) {
                  // Create updated item, preserving all existing properties
                  const updatedItem = { ...item }
                  
                  // Only update time properties if they exist in patch
                  // Preserve trim settings - they are independent of timeline duration
                  if (patch.start !== undefined || patch.duration !== undefined) {
                    updatedItem.time = {
                      ...item.time,
                      start: patch.start !== undefined ? patch.start : item.time?.start || item.start || 0,
                      duration: patch.duration !== undefined ? patch.duration : item.time?.duration || item.duration || 5
                    }
                  }
                  
                  // Preserve trim settings - never modify them during timeline operations
                  if (item.trim) {
                    updatedItem.trim = { ...item.trim }
                  }
                  
                  // Preserve settings - only update if explicitly in patch
                  if (patch.settings) {
                    updatedItem.settings = {
                      ...item.settings,
                      ...patch.settings
                    }
                  } else if (item.settings) {
                    updatedItem.settings = { ...item.settings }
                  }
                  
                  return updatedItem
                }
                return item
              })
            }
          : page
      )
    }
    settemplate_content(updatedTemplate)
  }

  const dragRef = useRef({ active:false })
  const playheadDragRef = useRef({ active: false })
  const [dragTooltip, setDragTooltip] = useState({ visible: false, x: 0, y: 0, text: '' })

  // Left padding for layer controls (matches the 128px width)
  const leftPadding = 128

  // Helper function to get preview content for timeline items
  const getItemPreview = (item) => {
    switch (item.type) {
      case 'text':
        const textContent = item.content || item.text || 'Text'
        return {
          type: 'text',
          content: textContent.length > 15 ? textContent.substring(0, 15) + '...' : textContent
        }
      case 'wordart':
        const wordartContent = item.content || item.text || 'WordArt'
        return {
          type: 'text',
          content: wordartContent.length > 12 ? wordartContent.substring(0, 12) + '...' : wordartContent
        }
      case 'image':
        return {
          type: 'image',
          src: item.src || item.url || item.storageUrl,
          alt: item.alt || item.name || 'Image'
        }
      case 'video':
        return {
          type: 'video',
          src: item.src || item.url || item.storageUrl,
          thumbnail: item.thumbnail || item.poster,
          alt: item.alt || item.name || 'Video'
        }
      case 'graphic':
      case 'svg-graphic':
        return {
          type: 'image',
          src: item.src || item.url || item.storageUrl || item.thumbnailUrl,
          alt: item.name || 'Graphic'
        }
      case 'shape':
        const shapeType = item.shapeType || 'rectangle'
        return {
          type: 'shape',
          shapeType: shapeType,
          fill: item.fill || item.color || item.properties?.fill || '#3b82f6',
          borderRadius: shapeType === 'circle' ? '50%' : (shapeType === 'rounded-rectangle' ? '4px' : '2px')
        }
      case 'mask':
        return {
          type: 'image',
          src: item.maskAsset?.thumbnailUrl || item.maskAsset?.storageUrl || item.maskAsset?.url,
          alt: 'Mask'
        }
      case 'adjustment-layer':
        return {
          type: 'adjustment-layer',
          content: item.name || 'Adjustment Layer',
          icon: '🎛️'
        }
      default:
        return {
          type: 'text',
          content: item.name || item.type || 'Item'
        }
    }
  }

  // Timeline Context Menu Functions
  const getTimelineItemContextMenuItems = (item, itemIndex, itemType = 'item') => {
    return [
      {
        label: 'Copy',
        icon: 'copy',
        action: () => {
          console.log('Copy timeline item:', item.id || item.name);
        },
        shortcut: 'Ctrl+C'
      },
      {
        label: 'Duplicate',
        icon: 'duplicate',
        action: () => {
          if (itemType === 'item') {
            // Duplicate visual item
            const duplicatedItem = {
              ...item,
              id: `${item.type}-${Date.now()}`,
              time: {
                ...item.time,
                start: (item.time?.start || 0) + (item.time?.duration || 2)
              }
            };

            const updatedTemplate = {
              ...template_content,
              pages: template_content.pages.map((page, pageIdx) =>
                pageIdx === currentPageIndex
                  ? { ...page, items: [...page.items, duplicatedItem] }
                  : page
              )
            };
            settemplate_content(updatedTemplate);
          } else if (itemType === 'audio') {
            // Duplicate audio item
            const duplicatedAudio = {
              ...item,
              id: `audio-${Date.now()}`,
              start: (item.start || 0) + (item.duration || 5)
            };

            const timeline = template_content.timeline || {};
            const updatedTemplate = {
              ...template_content,
              timeline: {
                ...timeline,
                audio: [...(timeline.audio || []), duplicatedAudio]
              }
            };
            settemplate_content(updatedTemplate);
          }
        },
        shortcut: 'Ctrl+D'
      },
      { type: 'separator' },
      {
        label: 'Trim Audio',
        icon: 'scissors',
        action: () => {
          if (itemType === 'audio') {
            setTrimAudioItem(item);
            setTrimDialogOpen(true);
          }
        },
        disabled: itemType !== 'audio'
      },
      {
        label: 'Split at Playhead',
        icon: 'cut',
        action: () => {
          if (itemType === 'item') {
            const itemStart = item.time?.start || 0;
            const itemEnd = itemStart + (item.time?.duration || 2);

            if (playhead > itemStart && playhead < itemEnd) {
              // Split the item
              const firstPart = {
                ...item,
                time: {
                  ...item.time,
                  duration: playhead - itemStart
                }
              };

              const secondPart = {
                ...item,
                id: `${item.type}-${Date.now()}`,
                time: {
                  ...item.time,
                  start: playhead,
                  duration: itemEnd - playhead
                }
              };

              const updatedTemplate = {
                ...template_content,
                pages: template_content.pages.map((page, pageIdx) =>
                  pageIdx === currentPageIndex
                    ? {
                        ...page,
                        items: page.items.map(it =>
                          it.id === item.id ? firstPart : it
                        ).concat(secondPart)
                      }
                    : page
                )
              };
              settemplate_content(updatedTemplate);
            }
          }
        },
        disabled: itemType === 'audio' || playhead <= (item.time?.start || 0) || playhead >= ((item.time?.start || 0) + (item.time?.duration || 2))
      },
      { type: 'separator' },
      // Move to options - flattened since ContextMenu doesn't support submenus
      ...((() => {
        const moveOptions = [];

        if (itemType === 'item') {
          // For visual items, show available layers
          const byLayer = new Map();
          items.forEach((it) => {
            const layer = (it.layer ?? 0);
            if (!byLayer.has(layer)) byLayer.set(layer, []);
            byLayer.get(layer).push(it);
          });

          const layers = Array.from(byLayer.keys()).sort((a, b) => a - b);
          const currentLayer = item.layer ?? 0;

          // Add separator before move options
          moveOptions.push({ type: 'separator' });

          // Add existing layers (except current)
          layers
            .filter(layer => layer !== currentLayer)
            .forEach(layer => {
              moveOptions.push({
                label: `Move to Layer ${layer}`,
                icon: 'layers',
                action: () => moveItemToLayer(item, itemType, layer)
              });
            });

          // Add option to create new layer
          moveOptions.push({
            label: `Move to New Layer ${Math.max(...layers, -1) + 1}`,
            icon: 'layers',
            action: () => moveItemToLayer(item, itemType, Math.max(...layers, -1) + 1)
          });

        } else if (itemType === 'audio') {
          // For non-visual items, show available tracks
          const currentTrackId = item.trackId ?? 0;

          // Add separator before move options
          moveOptions.push({ type: 'separator' });

          // Add existing tracks (except current)
          nonVisualTracks
            .filter(track => track.trackId !== currentTrackId)
            .forEach(track => {
              moveOptions.push({
                label: `Move to Audio Track ${track.trackId + 1}`,
                icon: 'move',
                action: () => moveItemToLayer(item, itemType, track.trackId)
              });
            });

          // Add option to create new track
          moveOptions.push({
            label: `Move to New Audio Track ${Math.max(...nonVisualTracks.map(t => t.trackId), -1) + 2}`,
            icon: 'move',
            action: () => {
              const newTrackId = Math.max(...nonVisualTracks.map(t => t.trackId), -1) + 1;
              moveItemToLayer(item, itemType, newTrackId);
            }
          });
        }

        return moveOptions;
      })()),
      { type: 'separator' },
      {
        label: 'Remove',
        icon: 'delete',
        action: () => {
          if (itemType === 'item') {
            const updatedTemplate = {
              ...template_content,
              pages: template_content.pages.map((page, pageIdx) =>
                pageIdx === currentPageIndex
                  ? { ...page, items: page.items.filter(it => it.id !== item.id) }
                  : page
              )
            };
            settemplate_content(updatedTemplate);
          } else if (itemType === 'audio') {
            // Remove audio item from page items
            const updatedTemplate = {
              ...template_content,
              pages: template_content.pages.map((page, pageIdx) =>
                pageIdx === currentPageIndex
                  ? { ...page, items: page.items.filter(it => it.id !== item.id) }
                  : page
              )
            };
            settemplate_content(updatedTemplate);
          }
        },
        danger: true,
        shortcut: 'Delete'
      }
    ];
  };

  // Add new audio track
  const addNewAudioTrack = () => {
    const maxTrackId = Math.max(-1, ...nonVisualTracks.map(t => t.trackId))
    const newTrackId = maxTrackId + 1

    console.log(`🎵 Creating new audio track ${newTrackId + 1}`)

    // The track will be created automatically when audio items are added to it
    // For now, we just log that a new track would be created
    // The actual track creation happens when items with the new trackId are added
  }

  // Create new layer if needed
  const ensureLayerExists = (layerIndex) => {
    // Layers are created automatically when items are assigned to them
    // No need to pre-create layer structure
    return layerIndex
  }

  // Removed drag and drop functions - no longer using up-down drag for layer changes

  // Function to move items to different layers/tracks
  const moveItemToLayer = (item, itemType, targetLayer) => {
    if (itemType === 'item') {
      // Move visual item to different layer
      const updatedTemplate = {
        ...template_content,
        pages: template_content.pages.map((page, pageIdx) =>
          pageIdx === currentPageIndex
            ? {
                ...page,
                items: page.items.map(it =>
                  it.id === item.id ? { ...it, layer: targetLayer } : it
                )
              }
            : page
        )
      };
      settemplate_content(updatedTemplate);
      console.log(`📋 Moved item "${item.name || item.type}" to layer ${targetLayer}`);
    } else if (itemType === 'audio') {
      // Move audio item to different track (now in page items)
      const updatedTemplate = {
        ...template_content,
        pages: template_content.pages.map((page, pageIdx) =>
          pageIdx === currentPageIndex
            ? {
                ...page,
                items: page.items.map(it =>
                  it.id === item.id ? { ...it, trackId: targetLayer } : it
                )
              }
            : page
        )
      };
      settemplate_content(updatedTemplate);
      console.log(`🎵 Moved audio "${item.name || 'Audio'}" to track ${targetLayer}`);
    }
  };

  const getTimelineContextMenuItems = () => {
    return [
      {
        label: 'Add Audio Track',
        icon: 'upload',
        action: () => {
          addNewAudioTrack();
        }
      },
      { type: 'separator' },
      {
        label: 'Reset Zoom',
        icon: 'reset',
        action: () => {
          setZoomSeconds(1);
        }
      }
    ];
  };

  const onDown = (e, type, idx, mode) => {
    e.stopPropagation();
    // Get the timeline tracks container for coordinate calculation
    const tracksContainer = document.getElementById('timeline-tracks-container')
    const containerRect = tracksContainer ? tracksContainer.getBoundingClientRect() : rootRef.current?.getBoundingClientRect()
    
    // Calculate mouse position relative to the timeline content area
    // Items are positioned relative to their parent container which is inside the tracks container
    let x = e.clientX - (containerRect?.left || 0)
    
    // Subtract leftPadding to get position relative to timeline content area (where items are positioned)
    // Visual items: positioned at start*pxPerSec relative to parent (after leftPadding)
    // Audio items: positioned at leftPadding + start*pxPerSec relative to parent (after leftPadding)
    if (type === 'audio') {
      x = x - leftPadding * 2
    } else {
      x = x - leftPadding
    }
    
    let start = 0, duration = 0
    if (type === 'item') {
      // Find the item by index in visualItems array (idx is originalIndex from visualItems)
      const visualItem = visualItems[idx]
      if (visualItem) {
        // Get the actual item from the full items array by ID
        const actualItem = items.find(it => it.id === visualItem.id)
        if (actualItem) {
          start = actualItem.time?.start || 0
          duration = actualItem.time?.duration || 2
        } else {
          // Fallback to visualItem data
          start = visualItem.time?.start || visualItem.start || 0
          duration = visualItem.time?.duration || visualItem.duration || 2
        }
      } else {
        // Fallback
        start = idx * 0.1
        duration = 2
      }
    } else {
      // For audio items, use nonVisualItems array
      const audioItem = nonVisualItems[idx]
      if (audioItem) {
        start = audioItem.time?.start || audioItem.start || 0
        duration = audioItem.time?.duration || audioItem.duration || 5
      } else {
        start = 0
        duration = 5
      }
    }
    
    // Calculate the item's current position in pixels (accounting for timelineStart offset)
    const itemStartX = (start - timelineStart) * pxPerSec
    
    // Calculate the offset from the item's left edge (where user clicked on the item)
    const offsetFromItemLeft = x - itemStartX
    
    // Store the offset so we can use it during drag
    dragRef.current = { active:true, type, idx, mode, start, duration, startX: x, offsetFromItemLeft: offsetFromItemLeft }

    // Show initial tooltip
    let tooltipText = ''
    if (mode === 'move') {
      tooltipText = `Start: ${start.toFixed(2)}s`
    } else if (mode === 'resize-l') {
      tooltipText = `Start: ${start.toFixed(2)}s | Duration: ${duration.toFixed(2)}s`
    } else if (mode === 'resize-r') {
      const endTime = start + duration
      tooltipText = `Duration: ${duration.toFixed(2)}s | End: ${endTime.toFixed(2)}s`
    }

    setDragTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY - 40,
      text: tooltipText
    })

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const onMove = (e) => {
    if (!dragRef.current.active) return
    
    // Get the timeline tracks container for coordinate calculation
    const tracksContainer = document.getElementById('timeline-tracks-container')
    const containerRect = tracksContainer ? tracksContainer.getBoundingClientRect() : rootRef.current?.getBoundingClientRect()
    
    // Calculate mouse position relative to the timeline content area
    let x = e.clientX - (containerRect?.left || 0)
    
    // Subtract leftPadding to get position relative to timeline content area
    // Visual items: positioned at start*pxPerSec relative to parent (after leftPadding)
    // Audio items: positioned at leftPadding + start*pxPerSec relative to parent (after leftPadding)
    if (dragRef.current.type === 'audio') {
      x = x - leftPadding * 2
    } else {
      x = x - leftPadding
    }
    
    let newStart = dragRef.current.start
    let newDuration = dragRef.current.duration
    
    if (dragRef.current.mode === 'move') {
      // Calculate new start position accounting for where user clicked on the item
      // The item's left edge should be at: mouseX - offsetFromItemLeft
      // Then convert back to time, accounting for timelineStart offset
      const itemLeftEdgeX = x - (dragRef.current.offsetFromItemLeft || 0)
      newStart = timelineStart + (itemLeftEdgeX / pxPerSec)
      newStart = Math.max(0, newStart)
    } else if (dragRef.current.mode === 'resize-l') {
      // Resize from left: mouse position becomes the new start (accounting for timelineStart)
      newStart = timelineStart + (x / pxPerSec)
      newStart = Math.max(0, newStart)
      const end = dragRef.current.start + dragRef.current.duration
      newDuration = Math.max(0.1, end - newStart)
    } else if (dragRef.current.mode === 'resize-r') {
      // Resize from right: mouse position becomes the new end (accounting for timelineStart)
      const newEnd = timelineStart + (x / pxPerSec)
      newDuration = Math.max(0.1, newEnd - dragRef.current.start)
    }
    
    // For resize operations, always snap to 0.25 second intervals
    const isResizeOperation = dragRef.current.mode === 'resize-l' || dragRef.current.mode === 'resize-r'
    if (isResizeOperation) {
      const snapInterval = 0.25
      newStart = Math.round(newStart / snapInterval) * snapInterval
      newDuration = Math.max(snapInterval, Math.round(newDuration / snapInterval) * snapInterval)
    } else if (snap && dragRef.current.mode === 'move') {
      // For move operations, only snap the start position, NOT the duration
      const grid = gridSize
      newStart = Math.round(newStart / grid) * grid
      // Duration should remain unchanged during move operations
      // newDuration = Math.max(grid, Math.round(newDuration / grid) * grid)
    }
    
    // Snap to neighbor clip edges and playhead
    if (snapToClips || snapToPlayhead) {
      const candidates = collectSnapPoints()
      if (snapToPlayhead) candidates.push(playhead)
      const threshold = isResizeOperation ? 0.125 : gridSize / 2 // Use 0.125 (half of 0.25) for resize operations
      
      if (dragRef.current.mode === 'move') {
        // For move operations, snap the start position
        const nearest = candidates.reduce((best, p) => Math.abs(p - newStart) < Math.abs(best - newStart) ? p : best, candidates[0])
        if (Math.abs(nearest - newStart) <= threshold) newStart = nearest
      } else if (dragRef.current.mode === 'resize-l') {
        // For resize-l, snap the start position
        const nearest = candidates.reduce((best, p) => Math.abs(p - newStart) < Math.abs(best - newStart) ? p : best, candidates[0])
        if (Math.abs(nearest - newStart) <= threshold) {
          newStart = nearest
          const end = dragRef.current.start + dragRef.current.duration
          newDuration = Math.max(0.25, end - newStart)
        }
      } else if (dragRef.current.mode === 'resize-r') {
        // For resize-r, snap the end position
        const end = newStart + newDuration
        const nearest = candidates.reduce((best, p) => Math.abs(p - end) < Math.abs(best - end) ? p : best, candidates[0])
        if (Math.abs(nearest - end) <= threshold) {
          newDuration = Math.max(0.25, nearest - newStart)
        }
      }
    }

    // Show drag tooltip with timing information
    let tooltipText = ''
    if (dragRef.current.mode === 'move') {
      tooltipText = `Start: ${newStart.toFixed(2)}s`
    } else if (dragRef.current.mode === 'resize-l') {
      tooltipText = `Start: ${newStart.toFixed(2)}s | Duration: ${newDuration.toFixed(2)}s`
    } else if (dragRef.current.mode === 'resize-r') {
      const endTime = newStart + newDuration
      tooltipText = `Duration: ${newDuration.toFixed(2)}s | End: ${endTime.toFixed(2)}s`
    }

    setDragTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY - 40, // Position above cursor
      text: tooltipText
    })

    // Update item based on operation type
    if (dragRef.current.mode === 'move') {
      // For move operations, only update start position - duration should remain unchanged
      if (dragRef.current.type === 'item') {
        setItemTime(dragRef.current.idx, { start: newStart })
      } else {
        setAudioClip(dragRef.current.idx, { start: newStart })
      }
    } else {
      // For resize operations, update both start and duration
      if (dragRef.current.type === 'item') {
        setItemTime(dragRef.current.idx, { start: newStart, duration: newDuration })
      } else {
        setAudioClip(dragRef.current.idx, { start: newStart, duration: newDuration })
      }
    }
  }

  const onUp = () => {
    dragRef.current = { active:false }
    playheadDragRef.current = { active: false }
    setDragTooltip({ visible: false, x: 0, y: 0, text: '' }) // Hide tooltip
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mousemove', onPlayheadMove)
    window.removeEventListener('mouseup', onUp)
  }

  // Handle audio trim save
  const handleTrimSave = (trimmedAudioItem) => {
    // Find and update the audio item in page items
    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, pageIdx) =>
        pageIdx === currentPageIndex
          ? {
              ...page,
              items: page.items.map(item =>
                item.id === trimmedAudioItem.id ? trimmedAudioItem : item
              )
            }
          : page
      )
    };

    settemplate_content(updatedTemplate);
    console.log('🎵 Audio trim applied:', trimmedAudioItem);
  }

  // Playhead drag handlers
  const onPlayheadDown = (e) => {
    e.stopPropagation()
    const rect = rootRef.current?.getBoundingClientRect()
    const x = e.clientX - (rect?.left || 0)
    playheadDragRef.current = { active: true, startX: x }

    // Pause playback while dragging
    if (playing) {
      setPlaying(false)
      onPlayingChange?.(false)
    }

    window.addEventListener('mousemove', onPlayheadMove)
    window.addEventListener('mouseup', onUp)
  }

  const onPlayheadMove = (e) => {
    if (!playheadDragRef.current.active) return
    const rect = rootRef.current?.getBoundingClientRect()
    const x = e.clientX - (rect?.left || 0)

    // Calculate time based on position, accounting for left padding and timelineStart
    const timelineX = x - leftPadding
    let newTime = timelineStart + (timelineX / pxPerSec)
    newTime = Math.max(0, Math.min(actualMaxEndTime, newTime))

    // Snap to grid if enabled
    if (snap) {
      newTime = Math.round(newTime / gridSize) * gridSize
    }

    // Snap to clip edges if enabled
    if (snapToClips) {
      const snapPoints = collectSnapPoints()
      const threshold = gridSize / 2
      const nearest = snapPoints.reduce((best, p) =>
        Math.abs(p - newTime) < Math.abs(best - newTime) ? p : best, snapPoints[0]
      )
      if (Math.abs(nearest - newTime) <= threshold) {
        newTime = nearest
      }
    }

    setPlayhead(newTime)
    onPlayheadChange?.(newTime)
  }

  const MIN_HEIGHT = 160
  const MAX_HEIGHT = 560
  const COLLAPSED_HEIGHT = 44
  const effectiveHeight = Math.min(MAX_HEIGHT, Math.max(height, MIN_HEIGHT))
  const containerHeight = isExpanded ? effectiveHeight : COLLAPSED_HEIGHT
  const resizeStateRef = useRef({ active: false, startY: 0, startHeight: effectiveHeight })

  const handleResizeMove = useCallback((event) => {
    if (!resizeStateRef.current.active || !onHeightChange) return
    const delta = resizeStateRef.current.startY - event.clientY
    const nextHeight = Math.min(
      MAX_HEIGHT,
      Math.max(MIN_HEIGHT, resizeStateRef.current.startHeight + delta)
    )
    onHeightChange(nextHeight)
  }, [onHeightChange])

  const handleResizeUp = useCallback(() => {
    if (!resizeStateRef.current.active) return
    resizeStateRef.current.active = false
    window.removeEventListener('mousemove', handleResizeMove)
    window.removeEventListener('mouseup', handleResizeUp)
  }, [handleResizeMove])

  const handleResizeMouseDown = useCallback((event) => {
    if (!onHeightChange || !isExpanded) return
    event.preventDefault()
    resizeStateRef.current = {
      active: true,
      startY: event.clientY,
      startHeight: effectiveHeight,
    }
    window.addEventListener('mousemove', handleResizeMove)
    window.addEventListener('mouseup', handleResizeUp)
  }, [effectiveHeight, handleResizeMove, handleResizeUp, isExpanded, onHeightChange])

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleResizeMove)
      window.removeEventListener('mouseup', handleResizeUp)
    }
  }, [handleResizeMove, handleResizeUp])

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl)
      }
    }
  }, [previewAudioUrl])

  return (
    <>
      {/* Hidden audio element for preview audio playback */}
      {previewAudioUrl && (
        <audio
          ref={previewAudioRef}
          src={previewAudioUrl}
          preload="auto"
          style={{ display: 'none' }}
        />
      )}
    <div
      ref={rootRef}
      className="w-full bg-gray-900 text-gray-100 border-t border-gray-700 transition-all duration-200 relative z-0 flex flex-col"
      style={{ height: containerHeight }}
    >
      {onHeightChange && (
        <div
          className={`h-2 w-full flex items-center justify-center border-b border-gray-800 ${isExpanded ? 'cursor-row-resize hover:bg-gray-800' : 'cursor-not-allowed'}`}
          onMouseDown={handleResizeMouseDown}
          title={isExpanded ? 'Drag to resize timeline height' : 'Expand timeline to resize'}
        >
          <div className="w-14 h-0.5 bg-gray-600 rounded-full" />
        </div>
      )}
      {/* Controls */}
      <div className="flex items-center gap-2 px-2 py-1 text-sm">
        {/* Expand/Collapse Button */}
        <button
          className="px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 flex items-center gap-1 text-xs"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Collapse Timeline' : 'Expand Timeline'}
        >
          <svg
            className={`w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          Timeline
        </button>
        <button className="px-1.5 py-0.5 rounded bg-blue-600 hover:bg-blue-500 text-xs" onClick={async ()=>{
          // Stop audio playback when using regular play
          if (playingWithAudio && previewAudioRef.current) {
            previewAudioRef.current.pause()
            setPlayingWithAudio(false)
            setPreviewAudioUrl(null)
          }
          
          const newPlaying = !playing
          setPlaying(newPlaying)
          onPlayingChange?.(newPlaying)
        }}>{playing?'Pause':'Play'}</button>
        <button 
          className={`px-1.5 py-0.5 rounded text-xs ${audioLoading ? 'bg-gray-500 cursor-not-allowed' : playingWithAudio ? 'bg-green-600 hover:bg-green-500' : 'bg-purple-600 hover:bg-purple-500'}`}
          disabled={audioLoading}
          onClick={async ()=>{
            if (playingWithAudio) {
              // Pause audio playback
              if (previewAudioRef.current) {
                previewAudioRef.current.pause()
              }
              setPlayingWithAudio(false)
              setPlaying(false)
              onPlayingChange?.(false)
              return
            }

            // Generate combined audio
            setAudioLoading(true)
            try {
              const page = template_content?.pages?.[currentPageIndex]
              if (!page) {
                throw new Error('No page found')
              }

              const payload = {
                template_content,
                page_index: currentPageIndex,
                duration: duration || 30.0
              }

              const response = await audioAPI.generatePreviewAudio(payload)
              
              // Create blob URL from response
              const blob = new Blob([response.data], { type: 'audio/mpeg' })
              const audioUrl = URL.createObjectURL(blob)
              
              setPreviewAudioUrl(audioUrl)
              
              // Wait for audio to be ready
              if (previewAudioRef.current) {
                previewAudioRef.current.src = audioUrl
                previewAudioRef.current.load()
                
                await new Promise((resolve, reject) => {
                  const audio = previewAudioRef.current
                  const handleCanPlay = () => {
                    audio.removeEventListener('canplay', handleCanPlay)
                    audio.removeEventListener('error', handleError)
                    resolve()
                  }
                  const handleError = (e) => {
                    audio.removeEventListener('canplay', handleCanPlay)
                    audio.removeEventListener('error', handleError)
                    reject(new Error('Failed to load audio'))
                  }
                  audio.addEventListener('canplay', handleCanPlay)
                  audio.addEventListener('error', handleError)
                })
              }

              // Start playback
              setPlayingWithAudio(true)
              setPlaying(true)
              onPlayingChange?.(true)
              
              if (previewAudioRef.current) {
                previewAudioRef.current.currentTime = playhead
                previewAudioRef.current.play().catch(err => {
                  console.error('Failed to play audio:', err)
                  setAudioLoading(false)
                })
              }
            } catch (error) {
              console.error('Failed to generate preview audio:', error)
              alert('Failed to generate preview audio. Please try again.')
            } finally {
              setAudioLoading(false)
            }
          }}
        >
          {audioLoading ? 'Processing...' : playingWithAudio ? 'Pause Audio' : 'Play with Audio'}
        </button>
        <button className="px-1.5 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-xs" onClick={()=>{
          setPlayhead(0);
          setPlaying(false);
          onPlayheadChange?.(0);
          onPlayingChange?.(false);
        }}>Restart</button>

        {/* Add Audio Track Button */}
        <button
          className="px-1.5 py-0.5 rounded bg-green-600 hover:bg-green-500 flex items-center gap-1 text-xs"
          onClick={addNewAudioTrack}
          title="Add new audio track"
        >
          <Plus className="w-2.5 h-2.5" />
          Add Audio Track
        </button>

        {/* Add Layer Button */}
        <button
          className="px-1.5 py-0.5 rounded bg-blue-600 hover:bg-blue-500 flex items-center gap-1 text-xs"
          onClick={() => {
            // Find the highest layer number and add 1
            const currentPage = template_content?.pages?.[currentPageIndex]
            const maxLayer = Math.max(-1, ...(currentPage?.items || []).map(item => item.layer || 0))
            const newLayer = maxLayer + 1

            // Create a placeholder item on the new layer (will be removed when user adds real content)
            const newItem = {
              id: `placeholder-${Date.now()}`,
              type: 'placeholder',
              layer: newLayer,
              time: { start: 0, duration: 1 },
              placeholder: true // Mark as placeholder
            }

            const updatedTemplate = {
              ...template_content,
              pages: template_content.pages.map((page, pageIdx) =>
                pageIdx === currentPageIndex
                  ? { ...page, items: [...(page.items || []), newItem] }
                  : page
              )
            }
            settemplate_content(updatedTemplate)
          }}
          title="Add new layer"
        >
          <Plus className="w-2.5 h-2.5" />
          Add Layer
        </button>

        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={loop} onChange={e=>setLoop(e.target.checked)} /> Loop</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={snap} onChange={e=>setSnap(e.target.checked)} /> Snap</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={showGrid} onChange={e=>setShowGrid(e.target.checked)} /> Grid</label>
        <label className="inline-flex items-center gap-2">Grid size
          <select className="bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-xs"
            value={gridSize}
            onChange={e=>setGridSize(parseFloat(e.target.value))}>
            <option value={0.05}>0.05s</option>
            <option value={0.1}>0.1s</option>
            <option value={0.25}>0.25s</option>
            <option value={0.5}>0.5s</option>
            <option value={1}>1s</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={snapToClips} onChange={e=>setSnapToClips(e.target.checked)} /> Snap to clips</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={snapToPlayhead} onChange={e=>setSnapToPlayhead(e.target.checked)} /> Snap to playhead</label>

        {/* Timeline Zoom and Start Controls - Seconds based */}
        <div className="flex items-center gap-2 ml-4">
          <span className="text-xs text-gray-400">Start:</span>
          <input
            type="number"
            step="0.1"
            min="0"
            max={actualMaxEndTime}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300 w-20"
            value={timelineStart}
            onChange={(e) => {
              const value = Math.max(0, Math.min(actualMaxEndTime, parseFloat(e.target.value) || 0))
              setTimelineStart(value)
            }}
            title="Timeline start offset in seconds"
          />
          <span className="text-xs text-gray-400">s</span>
          
          <span className="text-xs text-gray-400 ml-2">Zoom:</span>
          <select
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300"
            value={zoomSeconds}
            onChange={(e) => setZoomSeconds(parseFloat(e.target.value))}
            title="Select seconds per grid unit"
          >
            <option value={0.5}>0.5s</option>
            <option value={1}>1s</option>
            <option value={2}>2s</option>
            <option value={4}>4s</option>
            <option value={5}>5s</option>
            <option value={8}>8s</option>
            <option value={10}>10s</option>
            <option value={15}>15s</option>
            <option value={20}>20s</option>
            <option value={25}>25s</option>
          </select>
        </div>

        <div className="ml-auto text-gray-300">
          <span>{playhead.toFixed(2)}s / {actualMaxEndTime.toFixed(2)}s</span>
          <span className="ml-2 text-xs text-gray-400">
            (Video Length: {actualMaxEndTime >= 60 ? `${Math.floor(actualMaxEndTime/60)}:${(actualMaxEndTime%60).toFixed(0).padStart(2,'0')}` : `${actualMaxEndTime.toFixed(0)}s`})
          </span>
        </div>
      </div>

      {/* Collapsible Timeline Content */}
      {isExpanded && (
        <div className="flex-1 overflow-hidden flex flex-col relative">
          {/* Ruler - Fixed ruler that scrolls with tracks */}
          <div className="h-6 flex text-xs select-none relative overflow-hidden">
            {/* Left padding to align with track content (after layer controls) */}
            <div className="shrink-0 border-r border-gray-700" style={{ width: leftPadding }}></div>

            {/* Ruler container - no scrollbar, controlled by tracks scroll */}
            <div
              className="flex-1 overflow-hidden"
              id="timeline-ruler-container"
            >
              {/* Clickable timeline ruler - dynamic markers based on zoomSeconds */}
              <div
                className="flex cursor-pointer overflow-x-hidden"
                id="timeline-ruler-content"
                style={{ width: '100%' }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  // No horizontal scrolling, so calculate directly from click position
                  const x = e.clientX - rect.left - leftPadding
                  // Convert pixel position to time, accounting for timelineStart offset
                  let newTime = timelineStart + (x / pxPerSec)
                  newTime = Math.max(0, Math.min(actualMaxEndTime, newTime))

                  // Snap to grid if enabled
                  if (snap) {
                    newTime = Math.round(newTime / gridSize) * gridSize
                  }

                  setPlayhead(newTime)
                  onPlayheadChange?.(newTime)

                  // Pause playback when seeking
                  if (playing) {
                    setPlaying(false)
                    onPlayingChange?.(false)
                  }
                }}
              >
                {(() => {
                  // Calculate markers dynamically based on zoomSeconds and timelineStart
                  // Show markers from timelineStart to duration
                  const startMarker = Math.floor(timelineStart / zoomSeconds) * zoomSeconds
                  const endMarker = Math.ceil((timelineStart + duration) / zoomSeconds) * zoomSeconds
                  const markersToShow = Math.ceil((endMarker - startMarker) / zoomSeconds) + 1
                  
                  return Array.from({ length: markersToShow }).map((_, i) => {
                    const timeValue = startMarker + (i * zoomSeconds)
                    if (timeValue < timelineStart || timeValue > timelineStart + duration) return null
                    return (
                      <div key={i} style={{ flex: `0 0 ${pxPerSec * zoomSeconds}px`, minWidth: `${pxPerSec * zoomSeconds}px` }} className="border-r border-gray-700 text-center hover:bg-gray-800 transition-colors flex items-center justify-center shrink-0">
                        {timeValue}s
                      </div>
                    )
                  })
                })()}
              </div>
            </div>
          </div>

          {/* Grid overlay */}
          {showGrid && (
            <div className="absolute inset-x-0 bottom-0 top-6 pointer-events-none" style={{ backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px)`, backgroundSize: `${pxPerSec*0.5}px 100%` }} />
          )}

          {/* Tracks */}
          <div className="relative flex-1 overflow-hidden">
            {/* Tracks container with synchronized scrolling */}
            <div
              className="space-y-1 p-1 relative overflow-x-hidden overflow-y-auto h-full"
              id="timeline-tracks-container"
            >
              {/* Timeline content - no horizontal scrolling */}
              <div
                className="relative overflow-x-hidden"
                id="timeline-tracks-scroll"
              >
                <div
                  style={{ width: '100%', position: 'relative' }}
                  onClick={(e) => {
                    // Deselect items when clicking on empty timeline space
                    const clickedItem = e.target.closest('[data-timeline-item]');
                    if (!clickedItem) {
                      setSelectedElement(null);
                      setSelectedAudioTrack(null);
                    }
                  }}
                  onContextMenu={(e) => {
                    // Only show timeline context menu if not clicking on an item
                    const clickedItem = e.target.closest('[data-timeline-item]');
                    if (!clickedItem) {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedElement(null);
                      setSelectedAudioTrack(null);
                      showContextMenu(e, getTimelineContextMenuItems(), 'timeline');
                    }
                  }}
                >
                  {/* Playhead - Positioned to span all tracks, inside scrollable content */}
                  <div
                    className="absolute top-0 bottom-0 cursor-col-resize group pointer-events-none z-30"
                    style={{ 
                      left: `${leftPadding + (playhead - timelineStart) * pxPerSec}px`,
                      width: '1px',
                      pointerEvents: 'auto'
                    }}
                    onMouseDown={onPlayheadDown}
                  >
                    {/* Playhead line - spans full height */}
                    <div className="w-px h-full bg-red-500 group-hover:bg-red-400 transition-colors" />

                    {/* Playhead handle/scrubber */}
                    <div className="absolute -top-1 -left-2 w-4 h-3 bg-red-500 group-hover:bg-red-400 transition-colors cursor-col-resize flex items-center justify-center">
                      <div className="w-0 h-0 border-l-2 border-r-2 border-t-3 border-transparent border-t-red-500 group-hover:border-t-red-400 transition-colors" />
                    </div>

                    {/* Time tooltip on hover */}
                    <div className="absolute -top-8 -left-6 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                      {playhead.toFixed(2)}s
                    </div>
                  </div>

        {/* Visual tracks first */}
        {visualTracks.map(({ layer, items: layerItems }) => (
            <div key={`visual-layer-${layer}`}
                 className="h-5 relative bg-gray-800 rounded flex items-stretch mb-0.5">
              <div className="shrink-0 text-[10px] text-gray-300 flex items-center justify-between border-r border-gray-700 px-2" style={{ width: leftPadding }}>
                <span className="opacity-80">L{layer}</span>
                <div className="flex items-center gap-1">
                  <button
                    className={`px-1 py-0.5 rounded ${getLayerState(layer).muted ? 'bg-gray-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    title={getLayerState(layer).muted ? 'Unmute layer' : 'Mute layer'}
                    onClick={()=>setLayerState(layer, { muted: !getLayerState(layer).muted })}
                  >
                    🔇
                  </button>
                  <button
                    className={`px-1 py-0.5 rounded ${getLayerState(layer).locked ? 'bg-gray-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                    title={getLayerState(layer).locked ? 'Unlock layer' : 'Lock layer'}
                    onClick={()=>setLayerState(layer, { locked: !getLayerState(layer).locked })}
                  >
                    🔒
                  </button>
                </div>
              </div>
              <div className="relative flex-1">
                {/* Playhead line for this track */}
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-20"
                  style={{ left: `${(playhead - timelineStart) * pxPerSec}px` }}
                >
                  <div className="w-px h-full bg-red-500" />
                </div>

                {/* Empty layer indicator */}
                {layerItems.filter(item => !item.placeholder).length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-xs opacity-50 pointer-events-none">
                    Empty layer
                  </div>
                )}

                {layerItems.map((item, idx) => {
                  // Skip placeholder items in timeline display
                  if (item.placeholder) return null

                  const start = item.start || 0
                  const dur = item.duration || 2
                  const end = start + dur
                  const isVisibleAtPlayhead = playhead >= start && playhead <= end
                  return (
                    <div key={item.id}
                         data-timeline-item="true"
                         className={`absolute h-4 ${
                           item.type === 'adjustment-layer'
                             ? selectedElement === item.id
                               ? 'bg-purple-500 ring-2 ring-purple-300 border-2 border-purple-400'
                               : getLayerState(layer).locked
                                 ? 'bg-purple-900/40 cursor-not-allowed border border-purple-700'
                                 : isVisibleAtPlayhead
                                   ? 'bg-purple-600/80 hover:bg-purple-500 cursor-move border border-purple-400'
                                   : 'bg-purple-600/70 hover:bg-purple-500 cursor-move border border-purple-500'
                             : selectedElement === item.id
                               ? 'bg-blue-500 ring-2 ring-blue-300'
                               : getLayerState(layer).locked
                                 ? 'bg-blue-900/40 cursor-not-allowed'
                                 : isVisibleAtPlayhead
                                   ? 'bg-green-600/80 hover:bg-green-500 cursor-move'
                                   : 'bg-blue-600/70 hover:bg-blue-500 cursor-move'
                         } rounded text-xs flex items-center transition-all ${item.type === 'adjustment-layer' ? 'bg-gradient-to-r from-purple-600 to-purple-500' : ''}`}
                         style={{ left: (start - timelineStart)*pxPerSec, width: dur*pxPerSec, opacity: getLayerState(layer).muted ? 0.5 : 1 }}
                         onClick={(e) => {
                           e.stopPropagation();
                           setSelectedElement(item.id);
                           setSelectedAudioTrack(null); // Deselect audio when selecting item
                         }}
                         onMouseDown={(e)=>{ if (getLayerState(layer).locked) return; onDown(e,'item',item.originalIndex,'move') }}
                         onContextMenu={(e) => {
                           e.preventDefault();
                           e.stopPropagation();
                           setSelectedElement(item.id); // Select item when right-clicking
                           setSelectedAudioTrack(null);
                           showContextMenu(e, getTimelineItemContextMenuItems(item, item.originalIndex, 'item'), 'timeline-item');
                         }}>
                      <div className={`w-1.5 h-full ${getLayerState(layer).locked ? 'bg-white/10' : 'cursor-col-resize bg-white/40'}`} onMouseDown={(e)=>{ if (getLayerState(layer).locked) return; e.stopPropagation(); onDown(e,'item',item.originalIndex,'resize-l') }} />
                      {/* Item label/icon */}
                      <div className="flex-1 px-1 text-white text-[10px] truncate flex items-center gap-1">
                        {item.type === 'adjustment-layer' && (
                          <span className="text-xs">🎛️</span>
                        )}
                        <span className="truncate">{item.name || item.type || 'Item'}</span>
                        {item.type === 'adjustment-layer' && item.effects && item.effects.length > 0 && (
                          <span className="text-[8px] opacity-75">({item.effects.length})</span>
                        )}
                      </div>
                      {/* Effect overlays: fade and motion markers */}
                      <div className="absolute inset-0 pointer-events-none">
                        {(() => {
                          const fx = item.effects || []
                          const fade = fx.find(e => e.type === 'fade')
                          const motion = fx.find(e => e.type === 'motion')
                          return (
                            <>
                              {fade?.in > 0 && (
                                <div className="absolute top-0 left-0 h-1"
                                     style={{ width: (fade.in * pxPerSec), background: 'linear-gradient(to right, rgba(255,255,255,0.7), transparent)' }} />
                              )}
                              {fade?.out > 0 && (
                                <div className="absolute top-0 right-0 h-1"
                                     style={{ width: (fade.out * pxPerSec), background: 'linear-gradient(to left, rgba(255,255,255,0.7), transparent)' }} />
                              )}
                              {motion && (
                                <div className="absolute top-1 h-1 bg-purple-400/80 rounded"
                                     style={{ left: (((motion.start ?? start) - start) * pxPerSec), width: ((motion.duration ?? dur) * pxPerSec) }} />
                              )}
                            </>
                          )
                        })()}
                      </div>
                      {/* Selection indicator */}
                      {selectedElement === item.id && (
                        <div className="absolute inset-0 border-2 border-blue-400 rounded pointer-events-none" />
                      )}

                      {/* Item preview content */}
                      <div className="flex items-center gap-1 px-2 select-none relative z-10 min-w-0 flex-1">
                        {(() => {
                          const preview = getItemPreview(item)

                          if (preview.type === 'text') {
                            return (
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="text-xs opacity-60">📝</span>
                                <span className="text-xs truncate">{preview.content}</span>
                              </div>
                            )
                          } else if (preview.type === 'image') {
                            return (
                              <div className="flex items-center gap-1 min-w-0">
                                {preview.src ? (
                                  <img
                                    src={preview.src}
                                    alt={preview.alt}
                                    className="w-4 h-4 object-cover rounded flex-shrink-0"
                                    onError={(e) => {
                                      e.target.style.display = 'none'
                                      e.target.nextSibling.style.display = 'inline'
                                    }}
                                  />
                                ) : null}
                                <span className="text-xs opacity-60" style={{ display: preview.src ? 'none' : 'inline' }}>🖼️</span>
                                <span className="text-xs truncate">{preview.alt}</span>
                              </div>
                            )
                          } else if (preview.type === 'shape') {
                            return (
                              <div className="flex items-center gap-1 min-w-0">
                                <div
                                  className="w-3 h-3 flex-shrink-0 border border-white/20"
                                  style={{
                                    backgroundColor: preview.fill,
                                    borderRadius: preview.borderRadius
                                  }}
                                  title={preview.shapeType}
                                />
                                <span className="text-xs truncate capitalize">{preview.shapeType}</span>
                              </div>
                            )
                          }

                          return <span className="text-xs truncate">{item.name || item.type}</span>
                        })()}
                      </div>
                      <div className="w-1.5 h-full cursor-col-resize bg-white/40 ml-auto" onMouseDown={(e)=>{ e.stopPropagation(); onDown(e,'item',item.originalIndex,'resize-r') }} />
                    </div>
                  )
                })}
              </div>
            </div>
          ))
        }

        {/* Non-visual tracks (audio, sound effects) */}
        {nonVisualTracks.map(({ trackId, items: trackItems }) => (
          <div
            key={`audio-track-${trackId}`}
            className="h-8 relative bg-gray-800 rounded flex items-stretch mb-0.5"
          >
            <div
              className="shrink-0 text-[10px] text-gray-300 flex items-center justify-between border-r border-gray-700 px-2 h-full"
              style={{ width: leftPadding }}
            >
              <span>Audio {trackId + 1}</span>
              <div className="flex items-center gap-1">
                <button
                  className="px-1 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-xs"
                  title="Track settings"
                  onClick={() => console.log('Track settings for', trackId)}
                >
                  ⚙️
                </button>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.846 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.846l3.537-3.816a1 1 0 011.617.816zM16 8a2 2 0 11-4 0 2 2 0 014 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div className="flex-1 relative">
              {/* Playhead line for audio track content area */}
              <div
                className="absolute top-0 bottom-0 left-0 pointer-events-none z-20"
                style={{ left: `${(playhead - timelineStart) * pxPerSec}px` }}
              >
                <div className="w-px h-full bg-red-500" />
              </div>

              {trackItems.map((item, idx) => {
                const start = item.start || 0
                const dur = item.duration || 5
                const end = start + dur
                const isVisibleAtPlayhead = playhead >= start && playhead <= end
                return (
                  <div
                    key={item.id || idx}
                    data-timeline-item="true"
                    className={`absolute ${
                      selectedAudioTrack === item.originalIndex
                        ? 'bg-green-500 ring-2 ring-green-300'
                        : isVisibleAtPlayhead
                          ? 'bg-orange-600/80 hover:bg-orange-500'
                          : 'bg-green-600/70 hover:bg-green-500'
                    } rounded text-xs flex flex-col cursor-pointer transition-all`}
                    style={{
                      left: ((item.start || 0) - timelineStart) * pxPerSec,
                      width: (item.duration || 5) * pxPerSec,
                      height: '30px',
                      top: '2px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAudioTrack(item.originalIndex);
                      setSelectedElement(null); // Deselect visual items when selecting audio
                    }}
                    onMouseDown={(e) => onDown(e, 'audio', item.originalIndex, 'move')}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedAudioTrack(item.originalIndex); // Select audio when right-clicking
                      setSelectedElement(null);
                      showContextMenu(e, getTimelineItemContextMenuItems(item, item.originalIndex, 'audio'), 'timeline-audio');
                    }}
                  >
                    {/* Selection indicator */}
                    {selectedAudioTrack === idx && (
                      <div className="absolute inset-0 border-2 border-green-400 rounded pointer-events-none" />
                    )}

                    {/* Audio clip header */}
                    <div className="flex items-center h-6">
                      <div
                        className="w-1.5 h-full cursor-col-resize bg-white/40"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onDown(e, 'audio', item.originalIndex, 'resize-l');
                        }}
                      />
                      <div className="px-2 select-none truncate flex-1">{item.name || item.file?.name || 'Audio'}</div>
                      <div
                        className="w-1.5 h-full cursor-col-resize bg-white/40"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onDown(e, 'audio', item.originalIndex, 'resize-r');
                        }}
                      />
                    </div>

                    {/* Volume control */}
                    <div className="flex items-center px-2 h-4 bg-black/20">
                      <svg className="w-2 h-2 text-white/60" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.846 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.846l3.537-3.816a1 1 0 011.617.816zM16 8a2 2 0 11-4 0 2 2 0 014 0z" clipRule="evenodd" />
                      </svg>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={(item.settings?.volume || 1) * 100}
                        className="flex-1 mx-1 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #10b981 0%, #10b981 ${(item.settings?.volume || 1) * 100}%, #4b5563 ${(item.settings?.volume || 1) * 100}%, #4b5563 100%)`
                        }}
                        onChange={(e) => {
                          e.stopPropagation();
                          const volume = parseFloat(e.target.value) / 100;
                          setAudioClip(item.originalIndex, {
                            settings: {
                              ...(item.settings || {}),
                              volume: volume
                            }
                          });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <span className="text-[8px] text-white/60 w-6 text-right">
                        {Math.round((item.settings?.volume || 1) * 100)}%
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drag Tooltip - Shows timing information during drag operations */}
      {dragTooltip.visible && (
        <div
          className="fixed z-50 bg-black/90 text-white text-xs px-2 py-1 rounded pointer-events-none whitespace-nowrap"
          style={{
            left: dragTooltip.x,
            top: dragTooltip.y,
            transform: 'translateX(-50%)'
          }}
        >
          {dragTooltip.text}
        </div>
      )}

      {/* Context Menu */}
      <ContextMenu
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        menuItems={contextMenu.menuItems}
        contextType={contextMenu.contextType}
        onClose={hideContextMenu}
      />

      {/* Audio Trim Dialog */}
      <AudioTrimDialog
        isOpen={trimDialogOpen}
        onClose={() => {
          setTrimDialogOpen(false);
          setTrimAudioItem(null);
        }}
        audioItem={trimAudioItem}
        onSave={handleTrimSave}
      />
    </div>
    </>
  )
}
